// src/components/Feedback.js
import React, { useState, useContext } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

// AppContextをインポート (App.jsからエクスポートされています)
import { FirebaseContext } from '../App'; // App.jsのFirebaseContextをインポート

// =======================================================
// Feedback: ユーザーからのフィードバックを収集するコンポーネント
// =======================================================
const Feedback = () => {
    // FirebaseContextから必要な値を取得
    const { db, userId, firebaseAppId } = useContext(FirebaseContext); // Contextのキー名を合わせる
    const [feedbackText, setFeedbackText] = useState(''); // フィードバック入力の状態
    const [isSubmitting, setIsSubmitting] = useState(false); // 送信中の状態
    const [submissionMessage, setSubmissionMessage] = useState(''); // 送信結果メッセージ

    // フィードバック送信ハンドラ
    const handleSubmitFeedback = async () => {
        // フィードバックテキストが空の場合は送信しない
        if (!feedbackText.trim()) {
            setSubmissionMessage('フィードバックを入力してください。');
            return;
        }
        // 未認証の場合、またはFirestore/Authが準備できていない場合は送信しない
        // userIdがnullの場合は未認証と判断し、送信をブロック
        if (!userId || !db || !firebaseAppId) { // userIdがFirebaseContextから来ることを想定
            setSubmissionMessage('システムが準備できていません。しばらくお待ちください。');
            return;
        }

        setIsSubmitting(true); // 送信中状態に設定
        setSubmissionMessage(''); // メッセージをクリア

        try {
            // Firestoreのfeedbackコレクションへの参照
            const feedbackCollectionRef = collection(db, `artifacts/${firebaseAppId}/feedback`);

            // ドキュメントを追加
            await addDoc(feedbackCollectionRef, {
                userId: userId, // フィードバックを送信したユーザーID
                feedbackText: feedbackText, // フィードバック内容
                timestamp: serverTimestamp(), // サーバのタイムスタンプ
            });

            setSubmissionMessage('フィードバックを送信しました。ありがとうございます！'); // 成功メッセージ
            setFeedbackText(''); // テキストエリアをクリア
        } catch (error) {
            console.error('フィードバックの送信中にエラーが発生しました:', error);
            setSubmissionMessage(`フィードバックの送信に失敗しました: ${error.message}`); // エラーメッセージ
        } finally {
            setIsSubmitting(false); // 送信中状態を解除
        }
    };

    return (
        <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg shadow-inner mt-8 mx-auto max-w-2xl">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">フィードバック</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                このアプリに対するご意見、ご要望、バグ報告など、お気軽にお聞かせください。
            </p>
            <textarea
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 mb-3 resize-y min-h-[80px]"
                placeholder="ここにフィードバックを入力してください..."
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                rows="4" // 初期表示行数
                // userIdまたはdbがnullの場合はボタンを無効にする
                disabled={isSubmitting || !userId || !db}
            ></textarea>
            <button
                onClick={handleSubmitFeedback}
                // 送信中、テキストが空、またはuserId/dbがnullの場合はボタンを無効にする
                disabled={isSubmitting || !feedbackText.trim() || !userId || !db}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md shadow-md transition duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {isSubmitting ? '送信中...' : 'フィードバックを送信'}
            </button>
            {submissionMessage && (
                <p className={`mt-3 text-center text-sm ${submissionMessage.includes('失敗') ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    {submissionMessage}
                </p>
            )}
        </div>
    );
};

export default Feedback;
